import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { updateMetaTags, addJsonLd } from '../../utils/seo';
import { supabase } from '../../lib/supabase';

interface Article {
  id: number;
  title: string;
  excerpt: string;
  published_at: string;
  slug: string;
}

const MagazinePage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // SEO Meta Tags
    updateMetaTags({
      title: 'Vist Magazine - Décrypter les tendances qui façonnent notre époque',
      description: 'Culture, société, lifestyle et usages digitaux analysés avec recul et clarté. Découvrez notre ligne éditoriale et nos thématiques.',
      keywords: 'magazine digital, tendances digitales, culture société, lifestyle contemporain, analyse éditoriale, médias numériques',
      canonical: '/magazine'
    });

    // Schema.org JSON-LD for Magazine
    const baseUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
    addJsonLd({
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Vist Magazine - Notre ligne éditoriale",
      "url": `${baseUrl}/magazine`,
      "description": "Culture, société, lifestyle et usages digitaux analysés avec recul et clarté",
      "publisher": {
        "@type": "Organization",
        "name": "Vist Magazine",
        "logo": {
          "@type": "ImageObject",
          "url": "https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png"
        }
      },
      "mainEntity": {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "Qu'est-ce que Vist Magazine ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Vist Magazine est une publication éditoriale en ligne qui analyse les tendances culturelles, sociales et digitales avec recul et profondeur. Notre approche privilégie la compréhension des phénomènes plutôt que leur simple description."
            }
          },
          {
            "@type": "Question",
            "name": "Quels sujets sont abordés ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nous couvrons les tendances digitales et médias, la culture et société, le lifestyle contemporain, les nouveaux usages du web ainsi que les technologies et la création de contenu. Chaque sujet est traité avec une perspective analytique et contextuelle."
            }
          },
          {
            "@type": "Question",
            "name": "À qui s'adresse le magazine ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Vist Magazine s'adresse aux lecteurs curieux qui recherchent une compréhension approfondie des évolutions contemporaines, au-delà de l'actualité immédiate. Notre contenu intéresse particulièrement les professionnels de la communication, les créateurs de contenu et les observateurs des mutations sociales."
            }
          },
          {
            "@type": "Question",
            "name": "À quelle fréquence sont publiés les articles ?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Nous publions régulièrement de nouveaux contenus, en privilégiant la qualité et la profondeur d'analyse plutôt que la fréquence. Notre rythme de publication s'adapte à l'importance et à la complexité des sujets traités."
            }
          }
        ]
      }
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const { data, error } = await supabase
          .from('articles_cache')
          .select('id, title, excerpt, published_at, slug')
          .order('published_at', { ascending: false })
          .limit(6);

        if (error) throw error;
        setArticles(data || []);
      } catch (error) {
        console.error('Erreur lors du chargement des articles:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const themes = [
    {
      title: 'Tendances digitales & médias',
      icon: 'ri-smartphone-line',
      description: 'Évolution des plateformes, nouveaux formats et transformation des usages'
    },
    {
      title: 'Culture & société',
      icon: 'ri-book-open-line',
      description: 'Mouvements culturels, phénomènes sociaux et mutations contemporaines'
    },
    {
      title: 'Lifestyle contemporain',
      icon: 'ri-heart-3-line',
      description: 'Modes de vie, bien-être et nouvelles aspirations individuelles'
    },
    {
      title: 'Nouveaux usages du web',
      icon: 'ri-global-line',
      description: 'Pratiques émergentes, communautés en ligne et culture numérique'
    },
    {
      title: 'Technologies et création',
      icon: 'ri-lightbulb-line',
      description: 'Outils créatifs, innovation et impact sur les métiers du contenu'
    }
  ];

  const methodology = [
    {
      icon: 'ri-search-line',
      title: 'Recherche documentaire',
      description: 'Collecte et analyse approfondie des sources primaires et secondaires'
    },
    {
      icon: 'ri-shield-check-line',
      title: 'Vérification des sources',
      description: 'Validation systématique de la fiabilité et de la pertinence des informations'
    },
    {
      icon: 'ri-refresh-line',
      title: 'Mise à jour régulière',
      description: 'Actualisation continue des contenus pour maintenir leur pertinence'
    },
    {
      icon: 'ri-scales-line',
      title: 'Neutralité éditoriale',
      description: 'Approche équilibrée privilégiant l\'analyse objective aux opinions partisanes'
    }
  ];

  const faqs = [
    {
      question: 'Qu\'est-ce que Vist Magazine ?',
      answer: 'Vist Magazine est une publication éditoriale en ligne qui analyse les tendances culturelles, sociales et digitales avec recul et profondeur. Notre approche privilégie la compréhension des phénomènes plutôt que leur simple description.'
    },
    {
      question: 'Quels sujets sont abordés ?',
      answer: 'Nous couvrons les tendances digitales et médias, la culture et société, le lifestyle contemporain, les nouveaux usages du web ainsi que les technologies et la création de contenu. Chaque sujet est traité avec une perspective analytique et contextuelle.'
    },
    {
      question: 'À qui s\'adresse le magazine ?',
      answer: 'Vist Magazine s\'adresse aux lecteurs curieux qui recherchent une compréhension approfondie des évolutions contemporaines, au-delà de l\'actualité immédiate. Notre contenu intéresse particulièrement les professionnels de la communication, les créateurs de contenu et les observateurs des mutations sociales.'
    },
    {
      question: 'À quelle fréquence sont publiés les articles ?',
      answer: 'Nous publions régulièrement de nouveaux contenus, en privilégiant la qualité et la profondeur d\'analyse plutôt que la fréquence. Notre rythme de publication s\'adapte à l\'importance et à la complexité des sujets traités.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-[1280px] mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-[70px] md:h-[75px]">
            <Link to="/" className="flex items-center">
              <img src="https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png" alt="Vist Magazine" className="h-16 md:h-20" />
            </Link>
            
            <div className="hidden md:flex items-center space-x-10">
              <Link to="/" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Accueil</Link>
              <Link to="/magazine" className="text-[15px] text-black font-medium transition-colors whitespace-nowrap">Magazine</Link>
              <Link to="/blog" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Blog</Link>
              <Link to="/a-propos" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">À propos</Link>
              <Link to="/contact" className="text-[15px] text-[#2a2a2a] hover:text-black transition-colors whitespace-nowrap">Contact</Link>
            </div>

            <div className="flex items-center space-x-2 md:space-x-4">
              <button className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors cursor-pointer">
                <i className="ri-search-line text-xl text-[#2a2a2a]"></i>
              </button>
              <button 
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="w-10 h-10 flex items-center justify-center hover:bg-gray-100 rounded-full transition-colors md:hidden cursor-pointer"
              >
                <i className={`ri-${mobileMenuOpen ? 'close' : 'menu'}-line text-xl text-[#2a2a2a]`}></i>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100 shadow-lg">
            <div className="px-4 py-4 space-y-3">
              <Link to="/" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">Accueil</Link>
              <Link to="/magazine" className="block py-2 text-[15px] text-black font-medium">Magazine</Link>
              <Link to="/blog" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">Blog</Link>
              <Link to="/a-propos" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">À propos</Link>
              <Link to="/contact" className="block py-2 text-[15px] text-[#2a2a2a] hover:text-black transition-colors">Contact</Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Editorial */}
      <section className="pt-[140px] md:pt-[180px] pb-[60px] md:pb-[100px] px-4 md:px-8 bg-white">
        <div className="max-w-[900px] mx-auto text-center">
          <h1 className="font-serif text-[36px] md:text-[56px] leading-[1.15] text-[#1a1a1a] mb-4 md:mb-6">
            Vist Magazine – Décrypter les tendances qui façonnent notre époque
          </h1>
          <p className="text-[16px] md:text-[20px] text-[#555] leading-relaxed mb-8 md:mb-10">
            Culture, société, lifestyle et usages digitaux analysés avec recul et clarté.
          </p>
          <Link to="/blog" className="inline-flex items-center text-[14px] md:text-[15px] text-[#1a1a1a] hover:text-[#555] transition-colors cursor-pointer whitespace-nowrap">
            Découvrir les dernières publications
            <i className="ri-arrow-right-line ml-2"></i>
          </Link>
        </div>
      </section>

      {/* Ligne Éditoriale */}
      <section className="py-[60px] md:py-[100px] px-4 md:px-8 bg-[#fafafa]">
        <div className="max-w-[1280px] mx-auto">
          <div className="flex flex-col lg:flex-row gap-8 md:gap-12 items-center">
            <div className="lg:w-[45%] w-full">
              <img 
                src="https://public.readdy.ai/ai/img_res/c932e6c9c0c5ad4bea4f66ac4b3b8735.jpg"
                alt="Ligne éditoriale"
                className="w-full h-[300px] md:h-[500px] object-cover object-top rounded-2xl shadow-lg"
              />
            </div>
            <div className="lg:w-[55%] w-full">
              <h2 className="font-serif text-[32px] md:text-[42px] text-[#1a1a1a] mb-6 md:mb-8">Notre ligne éditoriale</h2>
              <div className="space-y-4 md:space-y-6 text-[15px] md:text-[16px] text-[#555] leading-relaxed">
                <p>
                  <strong className="text-[#1a1a1a]">Analyse plutôt que réaction.</strong> Dans un environnement saturé d'informations instantanées, nous prenons le temps d'examiner les phénomènes en profondeur, de contextualiser les événements et d'identifier les tendances durables au-delà du bruit médiatique.
                </p>
                <p>
                  <strong className="text-[#1a1a1a]">Pédagogie plutôt que buzz.</strong> Notre objectif est de rendre accessibles des sujets complexes sans simplification excessive. Nous privilégions la clarté et la rigueur pour permettre à nos lecteurs de développer leur propre compréhension des enjeux contemporains.
                </p>
                <p>
                  <strong className="text-[#1a1a1a]">Perspective long terme.</strong> Nous nous intéressons aux mutations structurelles plutôt qu'aux fluctuations éphémères. Cette approche nous permet d'offrir des contenus qui conservent leur pertinence au-delà de l'actualité immédiate.
                </p>
                <p>
                  <strong className="text-[#1a1a1a]">Sources croisées et contextualisation.</strong> Chaque publication s'appuie sur une recherche documentaire approfondie et une vérification systématique des informations. Nous croisons les perspectives pour offrir une vision équilibrée et nuancée.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Thématiques Couvertes */}
      <section className="py-[60px] md:py-[100px] px-4 md:px-8 bg-white">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="font-serif text-[32px] md:text-[42px] text-center text-[#1a1a1a] mb-[40px] md:mb-[60px]">Nos thématiques</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {themes.map((theme, index) => (
              <div key={index} className="bg-[#fafafa] p-6 md:p-8 rounded-xl hover:shadow-lg transition-all duration-300">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-black rounded-full mb-5 md:mb-6">
                  <i className={`${theme.icon} text-[24px] md:text-[28px] text-white`}></i>
                </div>
                <h3 className="font-semibold text-[18px] md:text-[20px] text-[#1a1a1a] mb-3 leading-[1.3]">
                  {theme.title}
                </h3>
                <p className="text-[14px] md:text-[15px] text-[#666] leading-relaxed">
                  {theme.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pourquoi Vist Magazine Existe */}
      <section className="py-[60px] md:py-[100px] px-4 md:px-8 bg-[#1a1a1a] text-white">
        <div className="max-w-[900px] mx-auto text-center">
          <h2 className="font-serif text-[32px] md:text-[42px] mb-6 md:mb-8">Pourquoi Vist Magazine existe</h2>
          <div className="space-y-4 md:space-y-6 text-[15px] md:text-[17px] leading-relaxed text-[#ccc]">
            <p>
              Nous vivons dans une époque paradoxale : jamais l'information n'a été aussi accessible, et pourtant la compréhension des phénomènes qui nous entourent n'a jamais été aussi fragmentée. L'instantanéité des flux d'actualité crée une illusion de connaissance sans permettre la réflexion nécessaire à une véritable compréhension.
            </p>
            <p>
              Vist Magazine est né de ce constat et du besoin de créer un espace où l'analyse prend le pas sur la réaction, où la profondeur remplace la superficialité. Notre ambition est de produire des contenus qui aident nos lecteurs à développer une vision claire et structurée des mutations contemporaines, qu'elles soient culturelles, sociales ou technologiques. Des contenus utiles, durables, et pensés pour éclairer plutôt que pour divertir.
            </p>
          </div>
        </div>
      </section>

      {/* Dernières Publications */}
      <section className="py-[60px] md:py-[100px] px-4 md:px-8 bg-white">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="font-serif text-[32px] md:text-[42px] text-center text-[#1a1a1a] mb-[40px] md:mb-[60px]">Dernières publications</h2>
          
          {loading ? (
            <div className="flex justify-center items-center py-12">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-black rounded-full animate-spin"></div>
            </div>
          ) : articles.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {articles.map((article) => (
                <Link 
                  key={article.id} 
                  to={`/blog/${article.slug}`}
                  className="border-b border-gray-200 pb-6 hover:border-black transition-colors cursor-pointer group"
                >
                  <h3 className="font-semibold text-[17px] md:text-[19px] text-[#1a1a1a] leading-[1.4] mb-3 group-hover:text-[#555] transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-[14px] md:text-[15px] text-[#666] leading-relaxed mb-4">
                    {article.excerpt}
                  </p>
                  <span className="text-[13px] text-[#999] whitespace-nowrap">{formatDate(article.published_at)}</span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[16px] text-[#666]">Aucun article publié pour le moment.</p>
            </div>
          )}
        </div>
      </section>

      {/* Méthodologie & Fiabilité */}
      <section className="py-[60px] md:py-[100px] px-4 md:px-8 bg-[#fafafa]">
        <div className="max-w-[1280px] mx-auto">
          <h2 className="font-serif text-[32px] md:text-[42px] text-center text-[#1a1a1a] mb-[40px] md:mb-[60px]">Notre approche éditoriale</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {methodology.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center bg-black rounded-full mx-auto mb-5 md:mb-6">
                  <i className={`${item.icon} text-[28px] md:text-[32px] text-white`}></i>
                </div>
                <h3 className="font-semibold text-[16px] md:text-[18px] text-[#1a1a1a] mb-3 whitespace-nowrap">
                  {item.title}
                </h3>
                <p className="text-[13px] md:text-[14px] text-[#666] leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Appel à Contribution */}
      <section className="py-[60px] md:py-[80px] px-4 md:px-8 bg-white">
        <div className="max-w-[900px] mx-auto text-center">
          <div className="bg-[#fafafa] p-8 md:p-12 rounded-2xl">
            <h2 className="font-serif text-[26px] md:text-[32px] text-[#1a1a1a] mb-4 md:mb-6">Contribuer à Vist Magazine</h2>
            <p className="text-[15px] md:text-[16px] text-[#555] leading-relaxed mb-6 md:mb-8">
              Vist Magazine est ouvert aux contributions éditoriales et aux analyses externes lorsque celles-ci s'inscrivent dans sa ligne éditoriale. Nous valorisons les perspectives originales et les approches analytiques qui enrichissent la compréhension des phénomènes contemporains.
            </p>
            <Link to="/contact" className="inline-flex items-center bg-black text-white px-6 md:px-8 py-3 md:py-3.5 rounded-full hover:bg-[#2a2a2a] transition-colors whitespace-nowrap cursor-pointer text-sm md:text-base">
              Proposer un article
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-[60px] md:py-[100px] px-4 md:px-8 bg-white">
        <div className="max-w-[900px] mx-auto">
          <h2 className="font-serif text-[32px] md:text-[42px] text-center text-[#1a1a1a] mb-[40px] md:mb-[60px]">Questions fréquentes</h2>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full flex items-center justify-between p-5 md:p-6 text-left hover:bg-[#fafafa] transition-colors cursor-pointer"
                >
                  <h3 className="font-semibold text-[16px] md:text-[18px] text-[#1a1a1a] pr-4">
                    {faq.question}
                  </h3>
                  <i className={`ri-arrow-${openFaq === index ? 'up' : 'down'}-s-line text-[24px] text-[#666] flex-shrink-0`}></i>
                </button>
                {openFaq === index && (
                  <div className="px-5 md:px-6 pb-5 md:pb-6">
                    <p className="text-[14px] md:text-[15px] text-[#555] leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1a1a1a] text-white">
        <div className="max-w-[1280px] mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 md:mb-12 pb-6 md:pb-8 border-b border-white/10">
            <img src="https://static.readdy.ai/image/612c2ae47a27268c7e802411c60f5af5/f6fb3f71d1e56eeefab047d5d014f9e4.png" alt="Vist Magazine" className="h-14 md:h-16 mb-4 md:mb-0" />
            <p className="font-serif italic text-[14px] md:text-[16px] text-[#aaa]">Analyses et regards éditoriaux sur le monde contemporain</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 mb-8 md:mb-12">
            <div>
              <h4 className="font-semibold text-[15px] md:text-[16px] mb-4 md:mb-5 whitespace-nowrap">Navigation</h4>
              <ul className="space-y-2 md:space-y-3">
                <li><Link to="/" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Accueil</Link></li>
                <li><Link to="/magazine" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Magazine</Link></li>
                <li><Link to="/blog" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Blog</Link></li>
                <li><Link to="/a-propos" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">À propos</Link></li>
                <li><Link to="/contact" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[15px] md:text-[16px] mb-4 md:mb-5 whitespace-nowrap">Légal</h4>
              <ul className="space-y-2 md:space-y-3">
                <li><Link to="/mentions-legales" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Mentions légales</Link></li>
                <li><Link to="/confidentialite" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Confidentialité</Link></li>
                <li><Link to="/conditions" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Conditions</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-[15px] md:text-[16px] mb-4 md:mb-5 whitespace-nowrap">Suivez-nous</h4>
              <ul className="space-y-2 md:space-y-3">
                <li><a href="#" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Facebook</a></li>
                <li><a href="#" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Twitter</a></li>
                <li><a href="#" className="text-[13px] md:text-[14px] text-[#999] hover:text-white transition-colors cursor-pointer whitespace-nowrap">Instagram</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-[13px] text-[#999]">© 2025 Vist Magazine. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MagazinePage;